import { proxy } from 'valtio';
import AppModule from '../../app/lib/AppModule';
import { UserStatusState } from './types';
import appManager from '@app/appManager';
import { getClient } from '@api/client';
import setUserStatusMutation from './data/setUserStatusMutation';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import { ApolloError } from '@apollo/client';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';

import { AuthUser } from '@modules/AuthModule/lib/types';
import { ServiceUserStatusEvent } from '@modules/ServiceModule/workspace';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import {
  ServiceSessionEventType,
  ServiceSessionWrapupEvent,
} from '@modules/ServiceModule/workspace/types/ServiceEvents';

/**
 * Defines the global state for the user status
 */
export const userStatusState = proxy<UserStatusState>({
  status: UserSetAvailabilityStatus.Offline,
  canChange: true,
  loading: false,
  error: undefined,
  wrapUpTimeEndsAt: undefined,
});

export class UserStatusModule extends AppModule {
  private userId: string | null = null;
  override events = ['userStatus.updated.available'];
  override preload(): void {
    this.app.events.on<ServiceUserStatusEvent>(
      'user.status.updated',
      ({ status, canChange }) => {
        this.updateState(this.userId, status, canChange, false, null, null);
      },
    );
    this.app.events.on('user.status.force_away', () => {
      this.setUserStatus(
        this.userId ?? '',
        UserSetAvailabilityStatus.Unavailable,
      );
    });
    this.app.events.on('auth.signin', (user: AuthUser) => {
      this.userId = user.id;
    });

    // listen to the wrap up started event to set the wrap up time and disable status change
    this.app.events.on<ServiceSessionWrapupEvent>(
      ServiceSessionEventType.WRAPUP_STARTED,
      (event) => {
        this.updateState(
          event.userId,
          null,
          false, // cannot change status during wrap up
          false,
          null,
          new Date(event.timestamp).getTime() + event.wrapupAllowedMs,
        );
      },
    );

    // listen to the wrap up completed event to enable status change
    this.app.events.on<ServiceSessionWrapupEvent>(
      ServiceSessionEventType.WRAPUP_COMPLETED,
      (event) => {
        this.updateState(
          event.userId,
          null,
          true, // can change status after wrap up
          false,
          null,
          null,
        );
      },
    );
  }

  /**
   * Updates the user status by calling the setUserStatus mutation and populates
   * the userStatusState with the corresponding loading and error.
   *
   * Note that the user status state update is done with websocket events
   * emitted by Kazoo. In this case, Recipient away and ready messages are
   * used to emit the "user.status.updated" event.
   *
   */
  public async setUserStatus(
    userId: string,
    status: UserSetAvailabilityStatus,
  ) {
    try {
      this.app.logger.debug('Setting user status', { userId, status });

      if (userId === this.userId && userStatusState.canChange === false) {
        throw new Error('User status cannot be changed');
      }

      // set loading state for user status
      this.updateState(userId, null, null, true, '', null);

      const { data } = await getClient().mutate({
        mutation: setUserStatusMutation,
        variables: { userId, status },
      });

      if (!data || !data.setUserStatus) {
        throw new Error('No user status');
      }

      // handle user errors by showing a toast and tracking the error
      data.setUserStatus &&
        this.updateState(userId, status, true, null, null, null);

      if (status === UserSetAvailabilityStatus.Available) {
        this.app.events.emit('userStatus.updated.available');
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        this.updateState(userId, null, null, false, error.message, null);
        DiagnosticsModule.reportUserError({
          message: error.message,
          priority: 'direct',
        });

        return;
      }
      throw new Error(extractErrorMessage(error));
    } finally {
      // reset loading state
      this.updateState(userId, null, null, false, null, null);
    }
  }

  private updateState(
    userId: string | null,
    status: UserSetAvailabilityStatus | null,
    canChange: boolean | null,
    loading: boolean | null,
    error: string | null,
    wrapUpEndsAt: number | null,
  ) {
    if (userId !== this.userId) {
      return;
    }

    if (canChange !== null) {
      userStatusState.canChange = canChange;
    }

    if (status) {
      userStatusState.status = status;
      userStatusState.error = undefined;
    }

    if (loading !== null) {
      userStatusState.loading = loading;
    }

    if (error !== null) {
      userStatusState.error = error;
    }

    if (wrapUpEndsAt !== null) {
      userStatusState.wrapUpTimeEndsAt = wrapUpEndsAt;
    }
  }
}

export default new UserStatusModule(
  appManager.events,
  appManager.logger('UserStatus'),
);
